import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import SlideReveal from './SlideReveal';
import KineticText from './KineticText';
import StatCardTransition from './StatCardTransition';
import ColorBlobBackground from './ColorBlobBackground';

export default function WrappedSequence({ steps, onComplete, loop = false }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex >= steps.length) {
      if (loop) {
        setCurrentIndex(0);
      } else if (onComplete) {
        onComplete();
      }
      return;
    }

    const currentStep = steps[currentIndex];
    const duration = currentStep.duration || 3000; // Default 3s per step

    const timer = setTimeout(() => {
      setCurrentIndex(prev => prev + 1);
    }, duration);

    return () => clearTimeout(timer);
  }, [currentIndex, steps, onComplete, loop]);

  const currentStep = steps[currentIndex % steps.length];

  if (!currentStep) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black text-white font-sans overflow-hidden">
      {/* Dynamic Background based on step */}
      <div className="absolute inset-0 transition-colors duration-700 ease-in-out"
           style={{ backgroundColor: currentStep.bgColor || 'transparent' }}>
         {!currentStep.bgColor && <ColorBlobBackground />}
      </div>
      
      <AnimatePresence mode="wait">
        <div key={currentIndex} className="w-full h-full flex items-center justify-center relative z-10">
          {currentStep.type === 'slide' && (
            <SlideReveal {...currentStep.props}>
              <div className="text-center max-w-4xl px-6">
                {currentStep.content}
              </div>
            </SlideReveal>
          )}
          
          {currentStep.type === 'text' && (
            <div className="relative z-10 text-center max-w-4xl px-6">
               <KineticText {...currentStep.props} />
            </div>
          )}

          {currentStep.type === 'stat' && (
            <div className="relative z-10 flex items-center justify-center w-full h-full p-6">
              <StatCardTransition {...currentStep.props}>
                {currentStep.content}
              </StatCardTransition>
            </div>
          )}
        </div>
      </AnimatePresence>
      
      {/* Progress Bar */}
      <div className="absolute top-0 left-0 w-full h-1 flex gap-1 px-1 pt-1 z-50">
        {steps.map((_, idx) => (
          <div key={idx} className="h-full flex-1 bg-white/20 rounded-full overflow-hidden">
            <div 
              className={`h-full bg-white transition-all duration-300 ease-linear ${
                idx < currentIndex ? 'w-full' : 
                idx === currentIndex ? 'w-full animate-progress' : 'w-0'
              }`}
              style={{
                transitionDuration: idx === currentIndex ? `${steps[idx].duration || 3000}ms` : '300ms'
              }}
            />
          </div>
        ))}
      </div>
      
      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .animate-progress {
          animation: progress linear forwards;
        }
      `}</style>
    </div>
  );
}
